import { createSelector } from "reselect"
import { chain, get, map, differenceWith, startCase, includes, isEmpty, toUpper, mapValues, size, capitalize, reduce } from "lodash"
import { getContentSets } from "selectors/contentSets"
import { getMissingDataFiles } from "selectors/missingDataFiles"
import { classificationsMap } from "helpers/classification"
import { makeCustomFactorFormatter } from "helpers/formatters/numbers"
import { CONTENT_SET_DATA_TYPES } from "helpers/contentSetConstants"
import { getModelFactors } from "selectors/modelMetadata"

const getCountries = state => get(state, "global.data.modelCountries.data")
const getCurrencies = state => get(state, "global.data.modelCurrencies.data")
const getRawCurrentSmartTradeValues = state => get(state, "modules.smartTradeRuleSearch.currentSmartTradeValues")
const getRawClassificationsValues = (state) => get(state, "global.data.classificationsValues.data")

export const getRawClassifications = (state) => get(state, "global.data.classifications.data.classifications")
export const getSelectedDate = (_, props) => get(props, "selectedDate")
export const getRuleDisplayType = (_, props) => get(props, "ruleType")
const getExistingSmartTradeRules = (_, props) => get(props, "existingRules")

const getClassificationsTiers = createSelector(
  [getRawClassifications, getSelectedDate],
  (classifications, selectedDate) => selectedDate ? classificationsMap(classifications, selectedDate) : null,
)
export const getClassifications = createSelector(
  [getRawClassificationsValues, getClassificationsTiers],
  (values, tiers) => mapValues(values, (c, key) =>
    getRules(get(c, "version.values"),
      chain(tiers)
        .find(({ id }) => id === key)
        .get("tiers", [])
        .value(),
    )),
)

const getAvailableBetas = createSelector(
  [getMissingDataFiles],
  (missingDataFiles) => {
    const betas = []
    if (!includes(missingDataFiles, "predictedBeta")) {
      betas.push("predicted")
    }
    if (!includes(missingDataFiles, "historicalBeta")) {
      betas.push("historical")
    }
    return betas
  },
)

const getCompositionItems = createSelector(
  [getCurrencies, getCountries],
  (currencies, countries) => {
    const compositionItems = {
      country: countries,
      currency: currencies,
    }
    return compositionItems
  },
)

export const getCurrentSmartTradeValues = createSelector(
  [getRawCurrentSmartTradeValues, getContentSets],
  (rawCurrentSmartTradeValues, rawContentSets) => {
    if (!rawCurrentSmartTradeValues) { return null }

    const factors = () => chain(rawCurrentSmartTradeValues)
      .get("exposure.0.factors")
      .groupBy("category")
      .mapValues(category => reduce(category, (factors, factor) => ({
        ...factors,
        [factor.id]: factor.net,
      }), {}))
      .value()

    const contentSets = () => reduce(rawContentSets, (contentSets, { id }) => ({
      ...contentSets,
      [id]: chain(rawCurrentSmartTradeValues)
        .get(`${id.replace(/-/gi, "_")}.0.factors`)
        .filter(({ value }) => value !== null)
        .reduce((factors, factor) => ({
          ...factors,
          [factor.id]: factor.value,
        }), {})
        .value(),
    }), {})

    const result = {
      composition: {
        ...chain(rawCurrentSmartTradeValues)
          .get("composition.0")
          .mapValues(category => reduce(category, (result, node) => ({
            ...result,
            [node.id]: node.economicExposure,
          }), {}))
          .value(),
        beta: get(rawCurrentSmartTradeValues, "beta.0"),
      },
      exposure: {
        factors: factors(),
        ...contentSets(),
      },
    }
    return result
  },
)

export const getAllRules = createSelector(
  [getModelFactors, getContentSets, getCompositionItems, getClassifications, getCurrentSmartTradeValues, getAvailableBetas, getRuleDisplayType, getExistingSmartTradeRules],
  (factorsMetadata, contentSets, compositionItems, classifications, currentSmartTradeValues, availableBetas, ruleDisplayType, existingRules) => {
    const makeValueGetter = (values, path, isCurrency) => {
      const formatter = makeCustomFactorFormatter(isCurrency)
      return formatter(get(values, path))
    }

    const getFactors = () => chain(factorsMetadata)
      .map(({ label, category }, id) => ({
        id,
        name: label,
        info: capitalize(category),
        type: "factor",
        category,
        value: makeValueGetter(currentSmartTradeValues, ["exposure", "factors", category, id]),
      }))
      .value()

    const getContentSets = () => chain(contentSets)
      .map(contentSet => {
        const isCurrency = contentSet.dataType === CONTENT_SET_DATA_TYPES.SENSITIVITY
        return map(contentSet.factors,
          ({ name, id }) => ({
            id,
            name,
            info: `Factor - ${contentSet.id}`,
            type: `factor-${contentSet.id}`,
            category: "factor",
            value: makeValueGetter(currentSmartTradeValues, ["exposure", contentSet.id, id], isCurrency),
            isCurrency,
            contentSetId: contentSet.id,
          }),
        )
      })
      .flatten()
      .value()

    const getBetas = () => map(availableBetas, (id) => ({
      id,
      name: capitalize(id),
      info: "Beta",
      type: "beta",
      category: "beta",
      value: makeValueGetter(currentSmartTradeValues, ["composition", "beta", id]),
    }))

    const getNonClassificationComposition = () => chain(compositionItems)
      .map((value, category) => {
        return map(value, (item) => ({
          id: item.value,
          name: item.label,
          info: startCase(category),
          type: category,
          category,
          value: makeValueGetter(currentSmartTradeValues, ["composition", category, item.value]),
        }))
      }).flatten()
      .value()

    const getClassificationComposition = () => chain(classifications)
      .map((value, classificationId) => {
        return map(value, (item) => ({
          id: item.value,
          name: item.label,
          info: getInfo(classificationId, item),
          type: "CLASSIFICATION",
          category: classificationId,
          value: makeValueGetter(currentSmartTradeValues, ["composition", `${classificationId}_${get(item, "tier.id")}`, item.value]),
          classificationId,
          classificationTier: get(item, "tier.id"),
        }))
      }).flatten()
      .value()

    let result
    if (ruleDisplayType === "exposure") {
      result = [
        ...getFactors(),
        ...getContentSets(),
      ]
    } else {
      result = [
        ...getNonClassificationComposition(),
        ...getClassificationComposition(),
        ...getBetas(),
      ]
    }

    return differenceWith(result, existingRules, isSameRule)
  },
)

export const getTabOrder = createSelector(
  [getRuleDisplayType],
  (ruleDisplayType) =>
    ruleDisplayType !== "exposure" ? {
      all: 0,
      country: 1,
      currency: 2,
      sector: 3,
      industry_group: 4,
      industry: 5,
    } : null,
)

function getRules(items, tiers, parent = []) {
  return chain(items)
    .map((r) => {
      if (r.values) {
        return [{
          value: r.id,
          label: r.name,
          parent,
          tier: tiers[size(parent)],
        }, getRules(r.values, tiers, [...parent, r.name])]
      }
      return {
        value: r.id,
        label: r.name,
        parent,
        tier: tiers[size(parent)],
      }
    })
    .flattenDeep()
    .value()
}

function getInfo(key, item) {
  return `${toUpper(key)}${!isEmpty(item.parent) ? ` - ${item.parent.join(" - ")}` : ""}`
}

function isSameRule(item1, item2) {
  return item1.id === item2.id && item1.type === item2.type && item1.contentSetId === item2.contentSetId
}
